import type {
    ApiResponse,
    BackendClientParameters,
} from "@internal/backstage-plugin-utility-common";
import { BackendClient } from "@internal/backstage-plugin-utility-common";

import type {
    GroupNormalizedAlertsData,
    NormalizedAlertsData,
    RefreshData,
    RefreshStatusData,
    SecurityInsightsHistoryData,
} from "../types";

/**
 * Construction options for {@link SecurityInsightsAggregatedClient}.
 *
 * Omits `targetPluginId` — the client always targets the `security-insights`
 * backend where the aggregated normalization routes are mounted.
 *
 * @public
 */
export type SecurityInsightsAggregatedClientOptions = Omit<
    BackendClientParameters,
    "targetPluginId"
>;

/**
 * Client for fetching server-side normalized security insights data from the
 * `security-insights` backend plugin.
 *
 * @public
 */
export class SecurityInsightsAggregatedClient extends BackendClient {
    constructor(options: SecurityInsightsAggregatedClientOptions) {
        super({ ...options, targetPluginId: "security-insights" });
    }

    /**
     * Fetches server-side normalized and SLA-calculated alerts for a single
     * entity. Calls all upstream sources (Dependabot, CodeQL, Secret
     * Scanning) in parallel and returns a unified alert list.
     */
    getNormalizedAlerts(
        kind: string,
        namespace: string,
        name: string
    ): Promise<ApiResponse<NormalizedAlertsData>> {
        return this.api<NormalizedAlertsData>(
            `entity/${kind}/${namespace}/${name}/current`
        );
    }

    /**
     * Fetches server-side normalized and SLA-calculated alerts aggregated
     * across every top-level entity a group owns, plus a per-entity
     * breakdown for driving the group's entity table.
     */
    getGroupCurrent(
        namespace: string,
        name: string
    ): Promise<ApiResponse<GroupNormalizedAlertsData>> {
        return this.api<GroupNormalizedAlertsData>(
            `group/${namespace}/${name}/current`
        );
    }

    /**
     * Triggers an on-demand re-scan of an entity's security data. Returns 202
     * immediately; poll `getRefreshStatus` to track progress.
     */
    triggerRefresh(
        kind: string,
        namespace: string,
        name: string
    ): Promise<ApiResponse<RefreshData>> {
        return this.api<RefreshData>(
            `entity/${kind}/${namespace}/${name}/refresh`,
            { method: "POST" }
        );
    }

    /**
     * Returns the status of the most recent refresh job for an entity. Returns
     * 404 if no job was found (either never triggered or evicted after 5 min).
     */
    getRefreshStatus(
        kind: string,
        namespace: string,
        name: string
    ): Promise<ApiResponse<RefreshStatusData>> {
        return this.api<RefreshStatusData>(
            `entity/${kind}/${namespace}/${name}/refresh/status`
        );
    }

    /**
     * Returns aggregated historical trend data across all entities a group
     * owns — daily snapshots summed across owned entities.
     */
    getGroupHistory(
        namespace: string,
        name: string,
        days?: number
    ): Promise<ApiResponse<SecurityInsightsHistoryData>> {
        const params = new URLSearchParams();
        if (days !== undefined) params.set("days", String(days));
        params.set("tz", Intl.DateTimeFormat().resolvedOptions().timeZone);
        return this.api<SecurityInsightsHistoryData>(
            `group/${namespace}/${name}/history?${params}`
        );
    }

    /**
     * Returns historical trend data for an entity — daily snapshots of alert
     * counts and severity breakdowns for the past `days` days (default: 90).
     */
    getHistory(
        kind: string,
        namespace: string,
        name: string,
        days?: number
    ): Promise<ApiResponse<SecurityInsightsHistoryData>> {
        const params = new URLSearchParams();
        if (days !== undefined) params.set("days", String(days));
        params.set("tz", Intl.DateTimeFormat().resolvedOptions().timeZone);
        return this.api<SecurityInsightsHistoryData>(
            `entity/${kind}/${namespace}/${name}/history?${params}`
        );
    }
}
